import * as React from 'react';
import { observer } from 'mobx-react';
import { format } from 'owa-strings';
import strings from 'owa-strings/owa-options-strings';
import { Label } from 'office-ui-fabric-react/lib/Label';
import {
    DetailsList,
    IColumn,
    SelectionMode,
    DetailsListLayoutMode,
    ColumnActionsMode,
    CheckboxVisibility,
    IDetailsRowProps,
} from 'office-ui-fabric-react/lib/DetailsList';
import { ActionButton, IconButton, ButtonType } from 'office-ui-fabric-react/lib/Button';
import { Dropdown, IDropdownOption } from 'office-ui-fabric-react/lib/Dropdown';
import { ISelection, Selection } from 'office-ui-fabric-react/lib/Selection';
import { Link } from 'office-ui-fabric-react/lib/Link';
import { connectedAccountsStore } from '../store/store';
import { getSelectedAccount } from '../selectors/getSelectedAccount';
import { selectConnectedAccount } from '../actions/connectedFull/selectConnectedAccount';
import { deleteConnectedAccount } from '../actions/connectedFull/deleteConnectedAccount';
import { resetConnectGmailStore } from '../actions/connectGmail/resetConnectGmailStore';
import { toggleConnectGmailWindow } from '../actions/connectGmail/toggleConnectGmailWindow';
import { resetConnectBasicStore } from '../actions/connectBasic/resetConnectBasicStore';
import { toggleConnectBasicWindow } from '../actions/connectBasic/toggleConnectBasicWindow';
import { resetEditAccountStore } from '../actions/editAccount/resetEditAccountStore';
import { selectDefaultFromAddress } from '../actions/defaultFrom/selectDefaultFromAddress';
import { markIsConnectedAccountsDirty } from '../actions/connectedFull/markIsConnectedAccountsDirty';
import { markIsDefaultFromDirty } from '../actions/connectedFull/markIsDefaultFromDirty';
import { loadMrsConnectedAccounts } from '../utils/loadMrsConnectedAccounts';
import { ControlIcons } from 'owa-control-icons';
import EditButton from './EditButton';
import ConnectGmailWindow from './ConnectGmailWindow';
import ConnectBasicWindow from './ConnectBasicWindow';
import ConnectAdvancedWindow from './ConnectAdvancedWindow';
import ConnectedAccountEditor from './ConnectedAccountEditor';

const styles = require('./ConnectedAccountsFull.scss');
const spriteStyles = require('owa-sprite/dist/styles/mail_sprite.css');
const classNames = require('classnames/bind');
const MAX_ALLOWED_ACCOUNTS = 20;
const MANAGE_ACCOUNT_URL = 'https://account.live.com/names/Manage';

@observer
export default class ConnectedAccountsFull extends React.Component<{}, {}> {
    private selection: ISelection;

    constructor(props: any) {
        super(props);
        this.selection = new Selection({
            onSelectionChanged: this.onSelectionChanged,
            getKey: this.getKey,
        });
    }

    private onAccountSelected = (index: number) => {
        selectConnectedAccount(index);

        let selected = getSelectedAccount();
        if (selected) {
            let syncRequest = selected.syncRequest;
            resetEditAccountStore(syncRequest);
        }
    };

    private onAccountDeselected = () => {
        let selected = getSelectedAccount();
        if (selected) {
            selectConnectedAccount(-1);
        }
    };

    private onSelectionChanged = () => {
        let selectedIndices = this.selection.getSelectedIndices();

        if (selectedIndices.length > 0) {
            this.onAccountSelected(selectedIndices[0]);
        } else {
            this.onAccountDeselected();
        }
    };

    private getKey = (item: any, index: number) => {
        return index;
    };

    private onGmailClicked = () => {
        resetConnectGmailStore();
        toggleConnectGmailWindow({ connectGmailOpen: true });
    };

    private onOtherClicked = () => {
        resetConnectBasicStore();
        toggleConnectBasicWindow({ connectBasicOpen: true });
    };

    private onDefaultFromAddressChanged = (address: IDropdownOption) => {
        if (address) {
            selectDefaultFromAddress(address.text);
            markIsDefaultFromDirty(true);
        }
    };

    private onEmailAliasClicked = () => {
        window.location.href = MANAGE_ACCOUNT_URL;
    };

    private onDeleteClicked = () => {
        let store = connectedAccountsStore();

        if (store.selected >= 0) {
            deleteConnectedAccount(store.selected);
            markIsConnectedAccountsDirty(true);
        }
    };

    private onRenderRow = (
        props?: IDetailsRowProps,
        defaultRender?: (props?: IDetailsRowProps) => JSX.Element | null
    ): JSX.Element | null => {
        if (defaultRender) {
            return <div className={styles.row}>{defaultRender(props)}</div>;
        }

        return null;
    };

    private onRenderItemColumn = (item?: any, index?: number, column?: IColumn): any => {
        if (column.key === 'command') {
            return (
                <div className={styles.commandCol}>
                    <EditButton onSelected={this.onAccountSelected} index={index} />
                    <IconButton
                        buttonType={ButtonType.command}
                        onClick={this.onDeleteClicked}
                        iconProps={{ iconName: ControlIcons.Delete }}
                        className={styles.command}
                        title={strings.options_connected_full_deleteButton}
                    />
                    <IconButton
                        buttonType={ButtonType.command}
                        onClick={loadMrsConnectedAccounts}
                        iconProps={{ iconName: ControlIcons.Refresh }}
                        className={styles.command}
                        title={strings.options_connected_full_refreshButton}
                    />
                </div>
            );
        }

        return item[column.key];
    };

    private get columns(): IColumn[] {
        return [
            {
                key: 'accountName',
                fieldName: 'accountName',
                name: strings.options_connected_full_accountColumn,
                minWidth: 150,
                maxWidth: 300,
                isResizable: false,
                columnActionsMode: ColumnActionsMode.disabled,
            },
            {
                key: 'lastUpdate',
                fieldName: 'lastUpdate',
                name: strings.options_connected_full_lastUpdateColumn,
                minWidth: 150,
                maxWidth: 250,
                isResizable: false,
                columnActionsMode: ColumnActionsMode.disabled,
            },
            {
                key: 'command',
                fieldName: 'command',
                name: '',
                minWidth: 150,
                maxWidth: 250,
                isResizable: false,
                columnActionsMode: ColumnActionsMode.disabled,
            },
        ];
    }

    private get emailAddressOptions(): IDropdownOption[] {
        let store = connectedAccountsStore();
        return store.defaultFromState.emailAddresses;
    }

    private get defaultSelectedAddress(): string {
        let store = connectedAccountsStore();

        for (let address of store.defaultFromState.emailAddresses) {
            if (address.text === store.defaultFromState.defaultFromAddress) {
                return address.key.toString();
            }
        }

        return null;
    }

    render() {
        let accounts = connectedAccountsStore().accounts;

        return (
            <div>
                <ConnectGmailWindow />
                <ConnectBasicWindow />
                <ConnectAdvancedWindow />
                <ConnectedAccountEditor />
                <div className={styles.section}>
                    <div>
                        {format(
                            strings.options_connected_full_addAccountTitle,
                            MAX_ALLOWED_ACCOUNTS
                        )}
                    </div>
                    <Label className={styles.title}>
                        {strings.options_connected_full_addAccountLabel}
                    </Label>
                    <div className={styles.actionButton}>
                        <span
                            className={classNames([
                                spriteStyles['mail-icon-connectgmail'],
                                styles.actionIcon,
                            ])}
                        />
                        <ActionButton
                            buttonType={ButtonType.command}
                            onClick={this.onGmailClicked}
                            className={styles.addAccountCommand}
                            disabled={accounts.length >= MAX_ALLOWED_ACCOUNTS}>
                            {strings.options_connected_full_connectGmailButton}
                        </ActionButton>
                    </div>
                    <div className={styles.actionButton}>
                        <span
                            className={classNames([
                                spriteStyles['mail-icon-connectothermail'],
                                styles.actionIcon,
                            ])}
                        />
                        <ActionButton
                            buttonType={ButtonType.command}
                            onClick={this.onOtherClicked}
                            className={styles.addAccountCommand}
                            disabled={accounts.length >= MAX_ALLOWED_ACCOUNTS}>
                            {strings.options_connected_full_otherAccountsButton}
                        </ActionButton>
                    </div>
                </div>
                <div className={styles.section}>
                    <Label className={styles.title}>
                        {strings.options_connected_full_manageAccountLabel}
                    </Label>
                    <DetailsList
                        className={styles.list}
                        items={accounts}
                        columns={this.columns}
                        selection={this.selection}
                        selectionMode={SelectionMode.single}
                        layoutMode={DetailsListLayoutMode.justified}
                        selectionPreservedOnEmptyClick={true}
                        checkboxVisibility={CheckboxVisibility.hidden}
                        onRenderRow={this.onRenderRow}
                        onRenderItemColumn={this.onRenderItemColumn}
                    />
                </div>
                <div className={styles.section}>
                    <Label className={styles.title}>
                        {strings.options_connected_full_changeFromAddressLabel}
                    </Label>
                    <Dropdown
                        className={styles.defaultFrom}
                        onChanged={this.onDefaultFromAddressChanged}
                        options={this.emailAddressOptions}
                        selectedKey={this.defaultSelectedAddress}
                    />
                </div>
                <div className={styles.section}>
                    <Label className={styles.title}>
                        {strings.options_connected_full_emailAliasesLabel}
                    </Label>
                    <Link onClick={this.onEmailAliasClicked}>
                        {strings.options_connected_full_manageEmailAliasLink}
                    </Link>
                </div>
            </div>
        );
    }
}
